/**
 * AIChatFab — Phase 3 (4B/4C). Floating chat assistant available on every
 * authenticated page. Bottom-right FAB → opens a 380px right-side panel.
 *
 * Page-context aware: passes the current pathname + record IDs from the URL
 * + user role so the AI knows what the user is looking at.
 *
 * Read-only Q&A only (per user choice, May 2026). No auto-action execution.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Sparkles, X, Send, Loader2, MessageSquare, History, Plus, Trash2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { useAuth, API } from "@/App";

const SESSION_LS_KEY = "ai_chat_session_id";

export default function AIChatFab() {
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]); // {role: user|assistant, content}
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_LS_KEY) || null; } catch { return null; }
  });
  const [view, setView] = useState("chat"); // "chat" | "history"
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const bottomRef = useRef(null);

  // Hide on auth/login routes — there's no user yet.
  const hideOn = ["/login", "/accept-invite", "/forgot-password", "/reset-password"];
  const visible = !!user && !hideOn.some(p => location.pathname.startsWith(p));

  // Auto-scroll on new message.
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Persist the active session id so reloads / navigation pick up the same convo.
  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem(SESSION_LS_KEY, sessionId);
      else localStorage.removeItem(SESSION_LS_KEY);
    } catch { /* storage unavailable — non-fatal */ }
  }, [sessionId]);

  // ── Hydrate previous session on first open ───────────────────────
  const hydrateSession = useCallback(async (sid) => {
    if (!sid || !token) return;
    try {
      const r = await axios.get(`${API}/ai/chat/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const msgs = (r.data?.messages || []).map((m) => ({
        role: m.role, content: m.content || "", actions: m.actions || [],
      }));
      setMessages(msgs);
    } catch (e) {
      // 404 → session no longer exists (or wrong user); start fresh.
      if (e?.response?.status === 404) {
        setSessionId(null); setMessages([]);
      }
    }
  }, [token]);

  // Load sessions list (for history view)
  const loadSessions = useCallback(async () => {
    if (!token) return;
    setSessionsLoading(true);
    try {
      const r = await axios.get(`${API}/ai/chat/sessions?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(r.data?.sessions || []);
    } catch { /* non-fatal */ }
    finally { setSessionsLoading(false); }
  }, [token]);

  // When the panel opens, hydrate a previously-saved session once.
  useEffect(() => {
    if (open && sessionId && messages.length === 0) {
      hydrateSession(sessionId);
    }
  }, [open, sessionId, hydrateSession]); // eslint-disable-line

  // Structured page-context payload sent to the backend on every turn so
  // the assistant can resolve record IDs → live MongoDB data (4C).
  const buildPageContextPayload = useCallback(() => {
    const path = location.pathname + (location.search || "");
    const m = {
      lead_id: params.leadId
        || /\/(lead-focus|leads)\/([^/?]+)/.exec(location.pathname)?.[2]
        || null,
      quote_id: params.quoteId
        || /\/quotes\/([^/?]+)(?:\/edit)?/.exec(location.pathname)?.[1]
        || null,
      contact_id: params.contactId
        || /\/contacts\/([^/?]+)/.exec(location.pathname)?.[1]
        || null,
    };
    const win = typeof window !== "undefined" ? (window.__crmPageContext || {}) : {};
    return {
      path,
      lead_id: m.lead_id || win.lead_id || null,
      quote_id: m.quote_id || win.quote_id || null,
      contact_id: m.contact_id || win.contact_id || null,
      visible_lead_ids: Array.isArray(win.visible_lead_ids) ? win.visible_lead_ids.slice(0, 20) : null,
      user_role: user?.role || null,
      user_name: user?.full_name || user?.name || null,
    };
  }, [location.pathname, location.search, params, user]);

  // Listen for cross-component requests (e.g. Dashboard's "Draft follow-ups").
  useEffect(() => {
    const onOpenWithPrompt = (ev) => {
      const prompt = ev?.detail?.prompt;
      if (!prompt) return;
      setOpen(true);
      setInput(prompt);
      setTimeout(() => {
        const fake = { role: "user", content: prompt };
        setMessages((m) => [...m, fake]);
        setBusy(true);
        axios.post(`${API}/ai/chat`, {
          message: prompt,
          history: [fake],
          page_context: buildPageContextPayload(),
          session_id: sessionId,
        }, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => {
            if (r.data?.session_id) setSessionId(r.data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: r.data?.response || "OK", actions: r.data?.actions || [] }]);
          })
          .catch(() => {
            setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit an error. Try rephrasing.", actions: [] }]);
          })
          .finally(() => { setBusy(false); setInput(""); });
      }, 80);
    };
    window.addEventListener("ai-chat:open-with-prompt", onOpenWithPrompt);
    return () => window.removeEventListener("ai-chat:open-with-prompt", onOpenWithPrompt);
  }, [token, buildPageContextPayload, sessionId]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ai/chat`, {
        message: text,
        history: next.slice(-6),
        page_context: buildPageContextPayload(),
        session_id: sessionId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.session_id) setSessionId(r.data.session_id);
      setMessages([...next, { role: "assistant", content: r.data?.response || "OK", actions: r.data?.actions || [] }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "Sorry — I hit an error. Try rephrasing or check back in a moment.", actions: [] }]);
    } finally {
      setBusy(false);
    }
  };

  // ── Session controls ─────────────────────────────────────────────
  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setView("chat");
  };
  const openHistory = async () => {
    setView("history");
    await loadSessions();
  };
  const pickSession = async (sid) => {
    setSessionId(sid);
    setMessages([]); // hydrateSession will refill
    await hydrateSession(sid);
    setView("chat");
  };
  const deleteSession = async (sid, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await axios.delete(`${API}/ai/chat/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (sessionId === sid) {
        setSessionId(null); setMessages([]);
      }
    } catch { /* non-fatal */ }
  };

  // Quick-action suggestions based on the current page.
  const quickActions = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/lead-focus/") || p.startsWith("/leads/")) return ["Summarize this lead in 3 bullets", "What's the AI score and why?", "What should my next step be?"];
    if (p.startsWith("/quotes/")) return ["Explain this quote to the prospect", "What objections might come up?"];
    if (p.startsWith("/templates")) return ["Suggest a template for cardiology + CCM", "Which templates have the highest reply rate?"];
    if (p.startsWith("/dialer") || p.startsWith("/communications")) return ["Coach me on the next call", "Top objection-handling lines"];
    if (p.startsWith("/leads")) return ["How many warm leads have I not contacted in 30 days?", "Top 3 hot leads to call now"];
    return ["What should I focus on today?", "Top 3 hot leads", "Draft a demo follow-up"];
  }, [location.pathname]);

  if (!visible) return null;

  return (
    <>
      {/* FAB — inline element inside the shared bottom-dock container in MainLayout */}
      <button
        data-testid="ai-chat-fab"
        aria-label="AI assistant"
        onClick={() => setOpen(true)}
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "linear-gradient(135deg, #0071e3 0%, #4c1d95 100%)",
          border: "none", cursor: "pointer", display: open ? "none" : "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 6px rgba(0,113,227,0.25)",
          color: "#fff", flexShrink: 0,
        }}>
        <Sparkles size={16} />
      </button>

      {/* Panel — portaled to document.body so it escapes the bottom-dock's
          containing block. The dock parent uses `backdrop-filter`, which
          per CSS spec creates a new containing block for ALL descendants
          including those with `position: fixed`. Without the portal, the
          panel's `top:0/right:0/height:100vh` end up relative to the dock
          pill instead of the viewport, and only a tiny corner of the
          header renders. */}
      {open && createPortal((
        <div
          data-testid="ai-chat-panel"
          style={{
            position: "fixed", right: 0, top: 0, height: "100vh", width: 380, maxWidth: "100vw",
            zIndex: 9999,
            background: "rgba(248,248,250,0.96)", backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            borderLeft: "0.5px solid rgba(0,0,0,0.08)",
            boxShadow: "-4px 0 32px rgba(0,0,0,0.10)",
            display: "flex", flexDirection: "column",
            fontFamily: '"DM Sans", -apple-system, sans-serif',
            animation: "aiPanelIn 220ms cubic-bezier(0.34,1.2,0.64,1)",
          }}>
          <style>{`@keyframes aiPanelIn { from { transform: translateX(20px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }`}</style>

          {/* Header */}
          <div style={{ padding: "14px 16px", borderBottom: "0.5px solid rgba(0,0,0,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {view === "history" ? (
                <button
                  data-testid="ai-chat-history-back"
                  onClick={() => setView("chat")}
                  aria-label="Back to chat"
                  style={{ width: 30, height: 30, borderRadius: 15, border: "none", background: "rgba(116,116,128,0.10)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <ArrowLeft size={15} />
                </button>
              ) : (
                <div style={{ width: 30, height: 30, borderRadius: 15, background: "linear-gradient(135deg, #0071e3 0%, #4c1d95 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Sparkles size={15} color="#fff" />
                </div>
              )}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1c1c1e" }}>
                  {view === "history" ? "Recent conversations" : "AI Assistant"}
                </div>
                <div style={{ fontSize: 10, color: "#6e6e73" }}>
                  {view === "history" ? `${sessions.length} saved` : `Context-aware · ${location.pathname}`}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {view === "chat" && (
                <>
                  <button
                    data-testid="ai-chat-new"
                    onClick={startNewSession}
                    aria-label="Start new chat"
                    title="Start new chat"
                    style={{ width: 26, height: 26, borderRadius: 13, border: "none", background: "rgba(116,116,128,0.10)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Plus size={13} />
                  </button>
                  <button
                    data-testid="ai-chat-history"
                    onClick={openHistory}
                    aria-label="Show history"
                    title="Show history"
                    style={{ width: 26, height: 26, borderRadius: 13, border: "none", background: "rgba(116,116,128,0.10)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <History size={13} />
                  </button>
                </>
              )}
              <button data-testid="ai-chat-close" onClick={() => setOpen(false)} style={{ width: 26, height: 26, borderRadius: 13, border: "none", background: "rgba(116,116,128,0.10)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={13} />
              </button>
            </div>
          </div>

          {/* History panel */}
          {view === "history" && (
            <div data-testid="ai-chat-history-list" style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {sessionsLoading && (
                <div style={{ padding: 18, textAlign: "center", color: "#6e6e73", fontSize: 11 }}>
                  <Loader2 size={11} style={{ animation: "spin 0.8s linear infinite", verticalAlign: "middle", marginRight: 5 }} />
                  Loading…
                </div>
              )}
              {!sessionsLoading && sessions.length === 0 && (
                <div data-testid="ai-chat-history-empty" style={{ padding: 28, textAlign: "center", color: "#6e6e73" }}>
                  <History size={26} style={{ opacity: 0.35, marginBottom: 8 }} />
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>No saved conversations yet</div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>Your chats will appear here once you send your first message.</div>
                </div>
              )}
              {!sessionsLoading && sessions.map((s) => {
                const isActive = sessionId === s.id;
                return (
                  <div
                    key={s.id}
                    data-testid={`ai-chat-session-${s.id}`}
                    onClick={() => pickSession(s.id)}
                    style={{
                      padding: "10px 12px", borderRadius: 10,
                      background: isActive ? "rgba(0,113,227,0.08)" : "#fff",
                      border: isActive ? "0.5px solid rgba(0,113,227,0.30)" : "0.5px solid rgba(0,0,0,0.08)",
                      cursor: "pointer", display: "flex", flexDirection: "column", gap: 3,
                      transition: "background 0.12s",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: "#1c1c1e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.title}
                      </div>
                      <button
                        data-testid={`ai-chat-session-delete-${s.id}`}
                        onClick={(ev) => deleteSession(s.id, ev)}
                        aria-label="Delete conversation"
                        style={{ width: 22, height: 22, borderRadius: 11, border: "none", background: "transparent", cursor: "pointer", color: "#8e8e93", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                    {s.preview && (
                      <div style={{ fontSize: 11, color: "#6e6e73", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.preview}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#aeaeb2", display: "flex", gap: 6 }}>
                      <span>{s.message_count} msg{s.message_count === 1 ? "" : "s"}</span>
                      <span>·</span>
                      <span>{relativeTime(s.updated_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Chat body (messages + input) — hidden while history view is up */}
          {view === "chat" && (
          <>
          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {messages.length === 0 && (
              <div data-testid="ai-chat-empty" style={{ padding: 16, textAlign: "center" }}>
                <MessageSquare size={28} style={{ opacity: 0.4 }} />
                <div style={{ fontSize: 12.5, color: "#6e6e73", marginTop: 8, marginBottom: 12 }}>Ask anything — I see {location.pathname.split("/")[1] || "the dashboard"} you're looking at.</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {quickActions.map((q, i) => (
                    <button key={i} data-testid={`ai-chat-quick-${i}`} onClick={() => { setInput(q); }} style={{ padding: "6px 10px", borderRadius: 8, border: "0.5px solid rgba(0,0,0,0.13)", background: "#fff", fontSize: 11.5, color: "#1c1c1e", cursor: "pointer", textAlign: "left" }}>
                      ✦ {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`ai-chat-msg-${m.role}`} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <div style={{
                  background: m.role === "user" ? "#0071e3" : "#fff",
                  color: m.role === "user" ? "#fff" : "#1c1c1e",
                  padding: "8px 12px", borderRadius: 14, fontSize: 12.5, lineHeight: 1.55,
                  whiteSpace: "pre-wrap", boxShadow: m.role === "user" ? "0 1px 3px rgba(0,113,227,0.25)" : "0 1px 3px rgba(0,0,0,0.06)",
                  border: m.role === "assistant" ? "0.5px solid rgba(0,0,0,0.08)" : "none",
                }}>{m.content}</div>
                {m.actions?.length > 0 && (
                  <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {m.actions.map((a, j) => (
                      <button key={j} data-testid={`ai-chat-action-${j}`} onClick={() => { if (a.action === "navigate" && a.path) { navigate(a.path); setOpen(false); } }} style={{ fontSize: 11, padding: "3px 9px", borderRadius: 14, background: "rgba(0,113,227,0.10)", color: "#0071e3", border: "0.5px solid rgba(0,113,227,0.2)", cursor: "pointer" }}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "#fff", borderRadius: 14, fontSize: 12, color: "#6e6e73", border: "0.5px solid rgba(0,0,0,0.08)" }}>
                <Loader2 size={11} style={{ animation: "spin 0.8s linear infinite" }} /> Thinking…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{ padding: 12, borderTop: "0.5px solid rgba(0,0,0,0.08)", display: "flex", gap: 6, alignItems: "flex-end", background: "rgba(255,255,255,0.65)" }}>
            <textarea
              data-testid="ai-chat-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              ref={(el) => { if (!el) return; el.style.height = "auto"; el.style.height = Math.min(Math.max(el.scrollHeight, 36), 120) + "px"; }}
              placeholder="Ask anything…"
              style={{ flex: 1, padding: "8px 11px", borderRadius: 10, border: "0.5px solid rgba(0,0,0,0.13)", background: "#fff", fontSize: 12.5, fontFamily: "inherit", outline: "none", resize: "none", minHeight: 36 }}
            />
            <button data-testid="ai-chat-send" onClick={send} disabled={!input.trim() || busy} style={{ width: 36, height: 36, borderRadius: 10, background: "#0071e3", color: "#fff", border: "none", cursor: input.trim() && !busy ? "pointer" : "not-allowed", opacity: input.trim() && !busy ? 1 : 0.4, display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(0,113,227,0.25)" }}>
              <Send size={13} />
            </button>
          </div>
          </>
          )}
        </div>
      ), document.body)}
    </>
  );
}

// Lightweight "5m ago / 3h ago / 2d ago / 2026-05-19" — avoids pulling
// in a date library just for the chat history list.
function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toISOString().slice(0, 10);
}
